import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';

import { useLazyQuery, useMutation } from '@apollo/client';
import { gqlDelete, gqlGetByIdQuery, gqlUpdate } from '../graphql';
import * as Lo from 'lodash';
import * as Yup from 'yup';
import {
  FormCard,
  YupNumeric,
  YupDate,
  YupString,
  promiseModalDialog,
  isNumeric,
  toastSuccess,
  YupObject,
  YupBoolean,
} from 'motion-components';
import FormFields from './order-form-fields';
import { useHandleError } from 'shared-pages/services';
import { isFotokiklosi, isRebatteryLiion, requiredString } from 'shared-pages/common/constants';
import { useTranslation } from 'react-i18next';
import { getRouteByName } from 'app/routes';
import { useRootStore } from 'store';
import { CollectionOrderStates } from 'shared-pages/common/constants';
import { observer } from 'mobx-react-lite';
import { useOrderXrb } from './xrb-approval-tabs-flow/use-order-xrb';
import { OrderFormSkeleton } from './order-form-skeleton';
import { useCollectionOrderCreate } from './use-collection-order-create';

const OrderFormPage = observer(() => {
  const { appStore } = useRootStore();
  const { id } = useParams<{ id: string }>();
  const { handleGraphqlError } = useHandleError('order');
  const [initialNewRecData, setInitialNewRecData] = useState<any>();
  const history = useHistory();
  const location: any = useLocation();

  const [updateRecord] = useMutation(gqlUpdate);
  const [deleteRecord] = useMutation(gqlDelete);

  const [get, { data: rawData, refetch, loading }] = useLazyQuery(gqlGetByIdQuery, {
    fetchPolicy: 'network-only',
  });
  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const isNew = idNum < 0;
  const showOKCANCELButtons = isNew;
  const data = rawData ? rawData[Object.keys(rawData)[0]] : undefined;
  const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));
  const { t } = useTranslation();

  const {
    xrbShape,
    formDataWithXrb,
    xrbLoading,
    fetchXrbByOrderId,
    getDefaultXrbForNewRecord,
    resetFormDataWithXrb,
  } = useOrderXrb({
    isNew,
    isRebatteryLiion,
    idNum,
    data,
    t,
  });

  const refetchOrderWithXrb = useCallback(async () => {
    await refetch?.();
    if (!isNew && isRebatteryLiion && idNum > 0) {
      await fetchXrbByOrderId(idNum);
    }
  }, [refetch, isNew, isRebatteryLiion, idNum, fetchXrbByOrderId]);

  const formShape = {
    // xrbData: YupObject.shape(xrbShape).nullable(),
    xrbData: isRebatteryLiion ? YupObject.shape(xrbShape).nullable() : YupObject.nullable(),
    id: YupNumeric,
    code: YupString,
    statusId: YupNumeric,
    orderedDate: YupDate.required(t(requiredString)),
    plannedCollectionDate: YupDate,
    collectionPointId: YupNumeric.required(t(requiredString)),
    collectorId: !isRebatteryLiion ? YupNumeric.required(t(requiredString)) : YupNumeric,
    contactPerson: YupString,
    notes: YupString,
    finDispNote: YupString,
    userString1: YupString, //Δρομολόγιο
    userDate1: YupDate,
    userString2: YupString,
    recordedById: YupNumeric,
    originatedFromId: YupNumeric,
    isUrgent: YupBoolean,
    collectionpoint: Yup.object()
      .shape({
        id: YupNumeric,
        name: YupString,
        addressLine1: YupString,
        addressCity: YupString,
        addressArea: YupString,
        postalCode: YupString,
      })
      .nullable(),
    collectionOrdersContainers: Yup.array().of(
      Yup.object().shape({
        id: YupNumeric,
        containertypeId: YupNumeric,
        containersQty: YupNumeric,
        containerType: Yup.object().shape({
          id: YupNumeric,
          name: YupString,
        }),
      }),
    ),
    routesLines: Yup.array().of(
      Yup.object().shape({
        id: YupNumeric,
        aa: YupNumeric,
        route: Yup.object().shape({
          id: YupNumeric,
          name: YupString,
        }),
      }),
    ),
    collection: YupObject.nullable(),
  };
  const { buildInitialNewRecord, createCollectionOrder, prepareCollectionOrderMutationValues } =
    useCollectionOrderCreate({ getDefaultXrbForNewRecord });

  /** Reset formDataWithXrb when navigating to different record */
  useEffect(() => {
    if (!isNew) {
      resetFormDataWithXrb();
    }
  }, [idNum, isNew, resetFormDataWithXrb]);

  /** Φόρτωση υπάρχουσας ή αρχικοποίηση νέας εγγραφής */
  useEffect(() => {
    if (!isNew) {
      get({ variables: { id: idNum } });
      return;
    }

    buildInitialNewRecord(location?.state?.collectionPointId, {
      originatedFromCollectionPoint: !!location?.state?.collectionPointId,
    }).then(setInitialNewRecData);
  }, [isNew, location?.state?.collectionPointId, idNum, buildInitialNewRecord]);

  const validationSchema = Yup.object().shape(formShape);
  type FormValuesType = Yup.InferType<typeof validationSchema>;

  function calcUserCanEdit() {
    let canEdit = appStore?.isPlatformOwner || isNew;
    if (isFotokiklosi) {
      if (appStore.isCollector) canEdit = true;
      else if (appStore.isCollectionPoint) canEdit = true;
    } else if (isRebatteryLiion) {
      if (appStore.isCollector) canEdit = true;
      else if (appStore.isCollectionPoint) canEdit = true;
    }
    const canEditReasoning = '';
    return { canEdit, canEditReasoning };
  }
  const { canEdit } = calcUserCanEdit();
  const canDelete = appStore.isPlatformOwner && data?.statusId === CollectionOrderStates.NotRouted;

  /**
   * * ΑΠΟΘΗΚΕΥΣΗ - UPDATE/CREATE
   */
  const handleSubmit = async (
    values: FormValuesType,
    formikHelpers: {
      validateForm: () => Promise<any>;
      setErrors: (errors: any) => void;
      setSubmitting: (v: boolean) => void;
    },
  ) => {
    console.log('$$$ handleSubmit', values, 'canEdit', canEdit);
    if (!canEdit) return;
    const { validateForm, setErrors, setSubmitting } = formikHelpers;
    const schemaErrors = await validateForm();
    if (Object.keys(schemaErrors ?? {}).length > 0) {
      setErrors(schemaErrors);
      setSubmitting(false);
      return;
    }

    const mutValues = prepareCollectionOrderMutationValues(values, {
      isRebatteryLiion,
      isNew,
      idNum,
    });

    if (isNew) {
      try {
        const id = await createCollectionOrder(values);
        toastSuccess(`Επιτυχημένη Προσθήκη Εγγραφής [${id}]`);
        history.push({ pathname: generatePath(getRouteByName('Order'), { id: id }) });
      } catch (error) {
        handleGraphqlError('mutation-add ', error);
      }
    } else {
      try {
        console.log('updateRecord', mutValues);
        const d = await updateRecord({
          variables: { data: { ...mutValues, id: idNum } },
          refetchQueries: ['getCollectionOrderXrbByOrderId'],
          awaitRefetchQueries: true,
        });
        toastSuccess(`Επιτυχημένη Ενημέρωση Εγγραφής [${d.data[Object.keys(d.data)[0]].id}]`);
        await refetch();
        if (isRebatteryLiion) {
          console.log('Fetching extention record xrb by order id ...');
          fetchXrbByOrderId(idNum);
        }
      } catch (error) {
        handleGraphqlError('mutation-add ', error);
      }
    }
  };
  const handleDelete = useCallback(async () => {
    const dlg = await promiseModalDialog({
      title: 'Διαγραφή Εγγραφής',
      text: 'Είστε σίγουροι ότι θέλετε να διαγράψετε την Εγγραφή;',
    });
    if (dlg) {
      try {
        const d = await deleteRecord({ variables: { id: idNum } });
        toastSuccess(`Επιτυχημένη Διαγραφή Εγγραφής [${d.data[Object.keys(d.data)[0]].id}]`);
        history.push({ pathname: generatePath(getRouteByName('Orders')) });
      } catch (error) {
        handleGraphqlError('mutation-delete', error);
      }
    }
  }, [deleteRecord, handleGraphqlError, history, idNum]);

  const isDataReady = useMemo(() => {
    if (isNew) return !!initialNewRecData;
    if (!isRebatteryLiion) return !loading && !!data;
    return !loading && !xrbLoading && !!formDataWithXrb?.xrbData;
  }, [
    data,
    formDataWithXrb?.xrbData,
    initialNewRecData,
    isNew,
    isRebatteryLiion,
    loading,
    xrbLoading,
  ]);

  return (
    <>
      {!isDataReady ? (
        <OrderFormSkeleton />
      ) : (
        <FormCard
          title={
            <span>{idNum === -1 ? 'Νέα Αίτηση' : `${t('Αίτηση Συλλογής')} : ${data?.code}`}</span>
          }
          loading={loading || (isRebatteryLiion && xrbLoading)}
          validationSchema={validationSchema}
          initialValues={isNew ? initialNewRecData : pickProperties(formDataWithXrb ?? data)}
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          permitSubmit={false}
          permitDelete={canDelete}
          enableReinitialize
          idx={idNum}
          alwaysOn={showOKCANCELButtons}
          // τα θέλω false για να θέτουν τα actionButtons τα errors στην isRebatteryLiion
          validateOnChange={false} // Να μην ελέγχει τα πεδία κατά την πληκτρολόγηση του χρήστη
          validateOnBlur={false} // Να μην ελέγχει τα πεδία κατά την έξοδο από το πεδίο
        >
          <FormFields
            isNew={isNew}
            data={data}
            updateRecord={undefined}
            refetch={refetchOrderWithXrb}
            loading={loading || (isRebatteryLiion && xrbLoading)}
          />
        </FormCard>
      )}
    </>
  );
});

export default OrderFormPage;
